import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";

import { Disabled } from "../../../../components/Disabled";
import { capitalizeAssociatedPress } from "../../../../helpers/string/capitalize";
import {
  INPUT_DEFAULT_STYLES,
  SM_LONG_INPUT_STYLES,
} from "../../detailedPages/styles";
import { NamedSelectOption, SelectOptionsProps, StyleVariant } from "../types";
import {
  BooleanVoidFunc,
  ChangeSelectHandler,
} from "../../../../helpers/functions/types";
import { INPUT_FIELD_STYLES } from "../../detailedPages/styles";

type SelectProps = {
  name: string;
  options: SelectOptionsProps;
  onChange?: ChangeSelectHandler;
  setShowAutosuggestions?: BooleanVoidFunc;
  value?: string;
  isColor?: boolean;
  positionStyles?: string;
  capitalize?: boolean;
  permissions?: string;
  disabled?: boolean;
  required?: boolean;
  styleVariant: StyleVariant;
};

const ATOM_SELECT_STYLES: Record<StyleVariant, string | undefined> = {
  newPage: `text-stone-700 dark:text-stone-300 w-full ${SM_LONG_INPUT_STYLES} ${INPUT_FIELD_STYLES} py-0.5`,
  detailPage: `h-10 ${INPUT_DEFAULT_STYLES}`,
  slideOver:
    "xs:max-w-[50%] block h-10 w-full justify-self-end truncate rounded-md border-stone-200 bg-stone-50 py-0.5 text-sm dark:border-none dark:bg-stone-700 dark:text-white",
  srLabel:
    "absolute z-50 left-0 w-full h-fit max-h-[200px] rounded-md bg-stone-50 px-2 text-sm text-stone-900 dark:bg-black dark:text-stone-100 solid border-stone-200 drop-shadow border",
};

const colorCode = (color: string) => {
  const code = color.includes("#") ? color.split("#")[1] : undefined;
  if (!code) return "";
  if (code.length !== 6 && code.length !== 3) return "";
  return code;
};

const enoughSpaceBottom = (inputId: string, listLength?: number) => {
  if (!listLength) return true;
  const matchingInput = document.getElementById(inputId) as HTMLInputElement;
  if (!matchingInput) return true;
  const { bottom } = matchingInput.getBoundingClientRect();
  const pageHeight = window.innerHeight;
  return pageHeight - Math.min(listLength, 5) * 30 >= bottom;
};

const mapOptions = (options: string[]): NamedSelectOption[] =>
  options.map((option) => ({ title: option, value: option }));

export const SelectAtom: FC<SelectProps> = ({
  name,
  options,
  onChange,
  setShowAutosuggestions,
  value,
  isColor,
  capitalize = true,
  permissions,
  disabled,
  required,
  styleVariant,
}) => {
  const [selectValue, setSelectValue] = useState(value);
  const handleChange = (target: HTMLSelectElement) => {
    setSelectValue(target.value);
    onChange?.(target);
  };

  useEffect(() => {
    setSelectValue(value);
  }, [value]);

  const actualName = setShowAutosuggestions ? `${name}List` : name;
  const positionStyles = useMemo(() => {
    if (!setShowAutosuggestions) return "";
    return enoughSpaceBottom(name, options?.length)
      ? "top-full"
      : "bottom-full";
  }, [options?.length, name, setShowAutosuggestions]);

  const mappedOptions =
    typeof options[0] === "string"
      ? mapOptions(options as string[])
      : (options as NamedSelectOption[]);

  const OptionsNodes = mappedOptions.map(
    ({ title, value, disabled }, index) => {
      const code = isColor && value ? colorCode(value) : "";
      return (
        <option
          style={
            code
              ? {
                  backgroundImage: `radial-gradient(closest-side circle at 95%, #${code}, #${code} 60%, #000000 70%, transparent 75%)`,
                }
              : undefined
          }
          className={
            styleVariant === "srLabel"
              ? "hover:bg-white dark:hover:bg-stone-600 rounded-sm py-1 h-6"
              : undefined
          }
          key={index}
          value={value}
          onFocus={({ target }) =>
            target.scrollIntoView({ block: "nearest", behavior: "auto" })
          }
          disabled={disabled}
        >
          {capitalize ? capitalizeAssociatedPress(title) : title}
        </option>
      );
    },
  );

  const onAction = setShowAutosuggestions //autosuggestions can not have onChange event because it triggers the parent input and closes the list, while controlled inputs require to have the onChange
    ? {
        onBlur: () => setShowAutosuggestions(false),
        onKeyUp: (event: React.KeyboardEvent<HTMLSelectElement>) => {
          event.preventDefault();
          event.stopPropagation();
          const { key, target } = event;
          if (key === "Enter") handleChange(target as HTMLSelectElement);
        },
        onClick: (event: React.MouseEvent<HTMLSelectElement, MouseEvent>) => {
          event.preventDefault();
          event.stopPropagation();
          handleChange(event.target as HTMLSelectElement);
        },
      }
    : {
        onChange: (event: ChangeEvent<HTMLSelectElement>) =>
          handleChange(event.target),
      };

  return (
    <select
      id={actualName}
      name={actualName}
      size={
        styleVariant === "srLabel" ? Math.min(options.length, 8) : undefined
      }
      value={selectValue}
      className={`${positionStyles} ${ATOM_SELECT_STYLES[styleVariant]} cursor-pointer disabled:cursor-not-allowed`}
      disabled={disabled}
      required={required}
      {...onAction}
    >
      {permissions ? (
        <Disabled editPermissions={permissions}>{OptionsNodes}</Disabled>
      ) : (
        OptionsNodes
      )}
    </select>
  );
};
